// src/utils/api-offers.js
import { supabase } from './supabase';

/* ================================================================
   Helpers
================================================================ */
function calcLineDiscountAmount(item = {}) {
  const base = Number(item.qty || 0) * Number(item.price || 0);
  const value = Math.max(0, Number(item.discount) || 0);
  const type = item.discount_type || "percentage";

  if (type === "fixed") return Math.min(base, value);
  return Math.min(base, base * (value / 100));
}

function calcTotals(services = [], vatRate = 0.21, discount = 0, discountType = "percentage") {
  const subtotal = services.reduce((sum, item) => {
    const base = Number(item.qty || 0) * Number(item.price || 0);
    return sum + Math.max(0, base - calcLineDiscountAmount(item));
  }, 0);
  const normalizedDiscount = Math.max(0, Number(discount) || 0);
  const discountAmount =
    discountType === "fixed"
      ? Math.min(normalizedDiscount, subtotal)
      : Math.min(subtotal, subtotal * (normalizedDiscount / 100));
  const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
  const tax = +(subtotalAfterDiscount * vatRate).toFixed(2);
  const total = +(subtotalAfterDiscount + tax).toFixed(2);
  return { subtotal, tax, total, discountAmount, subtotalAfterDiscount };
}

// Abreviar "Condición Comercial" para la grilla
function abbreviateCond({ payment_method, delivery_place, price_currency }) {
  const pay = (() => {
    const pm = (payment_method || '').toLowerCase();
    const m = pm.match(/(\d+)\s*d[ií]a?s?/);
    if (m) return `${m[1]}D`;
    if (pm.includes('contado')) return 'Contado';
    if (pm.includes('anticip')) return 'Anticipo';
    if (pm.includes('contra entrega')) return 'Contra entrega';
    return payment_method || '';
  })();

  const place = (() => {
    const d = (delivery_place || '').toLowerCase();
    if (d.includes('laboratorio')) return 'Ent. Lab';
    if (d.includes('sitio') || d.includes('planta') || d.includes('cliente')) return 'En sitio';
    return delivery_place || '';
  })();

  const curr = price_currency || '';
  return [pay, place, curr].filter(Boolean).join(' / ').slice(0, 60);
}

function safeText(value) {
  return String(value ?? "").trim();
}

function generateLocalId(prefix = "oc") {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeOcDocument(doc) {
  if (!doc || typeof doc !== "object") return null;

  const oc_number = safeText(doc.oc_number) || null;
  const file_url = safeText(doc.file_url) || null;
  const file_name = safeText(doc.file_name) || null;
  const file_type = safeText(doc.file_type) || null;
  const storage_path = safeText(doc.storage_path) || null;

  if (!oc_number && !file_url && !file_name && !file_type && !storage_path) {
    return null;
  }

  return {
    id: safeText(doc.id) || generateLocalId("oc"),
    oc_number,
    file_url,
    file_name,
    file_type,
    storage_path,
    created_at: safeText(doc.created_at) || null,
    updated_at: safeText(doc.updated_at) || null,
  };
}

export function normalizeOcDocuments(value) {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? (() => {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];

  return raw.map(normalizeOcDocument).filter(Boolean);
}

export function getLatestOcDocument(offer = {}) {
  const docs = normalizeOcDocuments(offer?.oc_documents);
  if (docs.length) return docs[docs.length - 1];

  const legacyUrl = safeText(offer?.oc_pdf_url);
  const legacyNumber = safeText(offer?.oc_number) || null;
  if (!legacyUrl && !legacyNumber) return null;

  return {
    id: `${safeText(offer?.id) || "legacy"}_oc`,
    oc_number: legacyNumber,
    file_url: legacyUrl || null,
    file_name: legacyUrl ? "Orden de compra" : null,
    file_type: legacyUrl && /\.pdf(\?.*)?$/i.test(legacyUrl) ? "application/pdf" : null,
    storage_path: null,
    created_at: safeText(offer?.updated_at) || safeText(offer?.created_at) || null,
    updated_at: safeText(offer?.updated_at) || safeText(offer?.created_at) || null,
  };
}

function getOcFileMeta(file) {
  const name = safeText(file?.name) || "archivo";
  const lower = name.toLowerCase();
  const mime = safeText(file?.type);

  if (mime === "application/pdf" || /\.pdf$/i.test(lower)) {
    return { ext: "pdf", contentType: "application/pdf" };
  }

  if (mime === "image/png" || /\.png$/i.test(lower)) {
    return { ext: "png", contentType: "image/png" };
  }

  if (mime === "image/jpeg" || mime === "image/jpg" || /\.(jpe?g)$/i.test(lower)) {
    return { ext: "jpg", contentType: "image/jpeg" };
  }

  throw new Error("El archivo debe ser PDF, JPG o PNG.");
}

async function uploadOcFile(offer, file, index = 0) {
  if (!offer?.id) throw new Error("Falta la oferta.");
  if (!file) throw new Error("Falta archivo.");

  const { ext, contentType } = getOcFileMeta(file);
  const bucket = "offers";
  const offerSlug = safeText(offer?.code || `offer_${offer?.id}`).replace(/[^\w\-]/g, "_") || "offer";
  const fileSlug = safeText(file.name)
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w\-]+/g, "_")
    .slice(0, 48) || "oc";
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const path = `oc/${offerSlug}/OC_${index + 1}_${stamp}_${fileSlug}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType,
    });

  if (upErr) {
    throw new Error(`No se pudo subir el archivo: ${upErr.message}`);
  }

  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  const file_url = pub?.publicUrl;
  if (!file_url) throw new Error("No se pudo obtener la URL pública del archivo.");

  return {
    file_url,
    file_name: safeText(file.name) || `OC_${index + 1}.${ext}`,
    file_type: contentType,
    storage_path: path,
  };
}

export async function saveOfferOcDocuments(offer, documents = []) {
  if (!offer?.id) throw new Error("Falta la oferta.");

  const currentDocs = normalizeOcDocuments(offer?.oc_documents);
  const inputDocs = Array.isArray(documents) ? documents : [];
  const cleaned = [];

  for (let i = 0; i < inputDocs.length; i += 1) {
    const row = inputDocs[i] || {};
    const previous = currentDocs.find((doc) => doc.id && row.id && doc.id === row.id) || null;
    const oc_number = safeText(row.oc_number) || previous?.oc_number || null;
    const existing = normalizeOcDocument(row);
    const file = row.file || null;

    let filePayload = {
      file_url: existing?.file_url || previous?.file_url || null,
      file_name: existing?.file_name || previous?.file_name || null,
      file_type: existing?.file_type || previous?.file_type || null,
      storage_path: existing?.storage_path || previous?.storage_path || null,
    };

    if (file) {
      filePayload = await uploadOcFile(offer, file, i);
    }

    if (!oc_number && !filePayload.file_url) {
      throw new Error(`La orden ${i + 1} necesita un número y un archivo.`);
    }

    cleaned.push({
      id: safeText(row.id) || previous?.id || generateLocalId("oc"),
      oc_number,
      file_url: filePayload.file_url || null,
      file_name: filePayload.file_name || null,
      file_type: filePayload.file_type || null,
      storage_path: filePayload.storage_path || null,
      created_at: safeText(row.created_at) || previous?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  if (!cleaned.length) {
    throw new Error("Agregá al menos una orden de compra.");
  }

  const latest = cleaned[cleaned.length - 1] || null;
  const patch = {
    oc_documents: cleaned,
    oc_number: latest?.oc_number ?? null,
    oc_pdf_url: latest?.file_url ?? null,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from("offers").update(patch).eq("id", offer.id);
  if (error) throw error;

  return { ok: true, oc_documents: cleaned, latest };
}

export async function uploadOfferOcDocument(offer, document = {}) {
  const base = normalizeOcDocuments(offer?.oc_documents);
  const next = [...base, document];
  return saveOfferOcDocuments(offer, next);
}

/* ================================================================
   LISTAR OFERTAS (con cliente, OC PDF, Cond. Com. y pdf_overrides)
   ✅ Busca por:
     - Nº oferta (base_number) optimizado para evitar overflow
     - CUIT con y sin guiones
     - Nombre cliente / Tax ID
     - Código (code) / Condición comercial
================================================================ */
export async function listOffers({ q = '', status = 'all', limit = 100, month = '', currency = 'all', clientName = '' } = {}) {
  const baseSelect = [
    'id,base_number,revision,code,status,invoice_number,billing_requested_date,invoiced_date,subtotal,tax,total,notes,created_at,updated_at',
    'discount,discount_type,accepted_via,oc_number,oc_pdf_url,oc_documents',
    'rejection_reason,rejected_at,rejected_by',
    'commercial_condition,validity_date,delivery_place,delivery_time,payment_method,price_currency,iva_rate,special_conditions',
    'pdf_overrides',
    'client:clients!offers_client_id_fkey(id,name,cuit,tax_id,address,locality,province,client_number)',
  ].join(',');

  const termRaw = (q || '').trim();
  const digitsOnly = termRaw.replace(/\D/g, ''); // saca guiones/espacios
  const isDigits = digitsOnly.length > 0 && /^\d+$/.test(digitsOnly);
  const looksLikeCuit = isDigits && digitsOnly.length === 11;
  const looksLikeOfferNumber = isDigits && digitsOnly.length <= 7;

  let query = supabase
    .from('offers')
    .select(baseSelect, { count: termRaw ? 'planned' : 'exact' })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (status && status !== 'all') query = query.eq('status', status);

  // Filtro por mes: month = "YYYY-MM"
  if (month) {
    const [y, m] = month.split('-');
    const from = `${y}-${m}-01`;
    const lastDay = new Date(Number(y), Number(m), 0).getDate();
    const to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    query = query.gte('created_at', from).lte('created_at', `${to}T23:59:59`);
  }

  // Filtro por moneda
  if (currency && currency !== 'all') query = query.eq('price_currency', currency);

  // Filtro por nombre de cliente (lookup previo)
  if (clientName.trim()) {
    try {
      const { data: clientMatches } = await supabase
        .from('clients')
        .select('id')
        .ilike('name', `%${clientName.trim()}%`)
        .limit(50);
      const ids = (clientMatches || []).map((c) => c.id).filter(Boolean);
      if (ids.length) query = query.in('client_id', ids);
      else return { data: [], count: 0 }; // ningún cliente coincide
    } catch {
      return { data: [], count: 0 };
    }
  }

  if (!termRaw) {
    const { data, error, count } = await query;
    if (error) {
      console.error('listOffers error:', error);
      throw new Error('No se pudieron cargar las ofertas.');
    }
    return {
      data: (data ?? []).map((row) => ({
        ...row,
        oc_documents: normalizeOcDocuments(row?.oc_documents),
      })),
      count: count || 0,
    };
  }

  // Heurística:
  // - Nº de oferta suele ser corto (ej 6512)
  // - CUIT es 11 dígitos (ej 30508598501)
  // Formato CUIT con guiones (30-xxxxxxxx-x)
  const cuitDashed =
    looksLikeCuit ? `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 10)}-${digitsOnly.slice(10)}` : null;

  // 1) Buscar IDs de clientes por nombre / CUIT / tax_id.
  //    Evitamos esta consulta si el término es claramente numérico y no parece CUIT.
  let clientIds = [];
  const shouldSearchClients =
    !isDigits ||
    looksLikeCuit ||
    (termRaw.length >= 3 && /[a-zA-Z]/.test(termRaw));

  if (shouldSearchClients) {
    try {
      const clientOrParts = [
        `name.ilike.%${termRaw}%`,
        `cuit.ilike.%${termRaw}%`,
        `tax_id.ilike.%${termRaw}%`,
      ];

      // si parece CUIT en dígitos, también buscar en formato con guiones
      if (cuitDashed) {
        clientOrParts.push(`cuit.ilike.%${cuitDashed}%`);
        clientOrParts.push(`tax_id.ilike.%${digitsOnly}%`);
        clientOrParts.push(`tax_id.ilike.%${cuitDashed}%`);
      }

      const { data: clients, error: cErr } = await supabase
        .from('clients')
        .select('id')
        .or(clientOrParts.join(','))
        .limit(80);

      if (!cErr && Array.isArray(clients)) {
        clientIds = clients.map((c) => c.id).filter(Boolean);
      }
    } catch (e) {
      console.warn('[listOffers] clients lookup warning:', e);
      clientIds = [];
    }
  }

  // 2) OR con columnas de OFFERS
  const orParts = [
    `code.ilike.%${termRaw}%`,
    `commercial_condition.ilike.%${termRaw}%`,
    `payment_method.ilike.%${termRaw}%`,
  ];

  // ✅ SOLO si parece Nº de oferta (corto)
  if (looksLikeOfferNumber) {
    orParts.push(`base_number.eq.${digitsOnly}`);
    // si oc_number es numeric en tu schema, esto también sirve con números cortos
    orParts.push(`oc_number.eq.${digitsOnly}`);
  }

  // ✅ Si hay clientes, agregamos match por client_id (esto NO rompe)
  if (clientIds.length) {
    orParts.push(`client_id.in.(${clientIds.join(',')})`);
  }

  query = query.or(orParts.join(','));

  const { data, error, count } = await query;
  if (error) {
    console.error('listOffers error:', error);
    throw new Error('No se pudieron cargar las ofertas.');
  }
  return {
    data: (data ?? []).map((row) => ({
      ...row,
      oc_documents: normalizeOcDocuments(row?.oc_documents),
    })),
    count: count || 0,
  };
}

/* ================================================================
   OBTENER OFERTA (con renglones, Cond. Com. y pdf_overrides)
   >>> Incluye: service_type, certification, contact_name, contact_email
================================================================ */
export async function getOffer(offerId) {
  const { data: offer, error: errOffer } = await supabase
    .from('offers')
    .select(
      [
        'id,base_number,revision,code,status,invoice_number,billing_requested_date,invoiced_date,subtotal,tax,total,notes,created_at,updated_at',
        'discount,discount_type,accepted_via,oc_number,oc_pdf_url,oc_documents',
        'rejection_reason,rejected_at,rejected_by',
        'service_type,certification,contact_name,contact_email',
        'commercial_condition,validity_date,delivery_place,delivery_time,payment_method,price_currency,iva_rate,special_conditions',
        'pdf_overrides',
        'client:clients!offers_client_id_fkey(id,name,cuit,tax_id,email,phone,address,locality,province,client_number)',
      ].join(',')
    )
    .eq('id', offerId)
    .single();
  if (errOffer) throw errOffer;

  const { data: services, error: errServ } = await supabase
    .from('offer_services')
    .select('id,instrument_id,description,qty,price,discount,discount_type,position')
    .eq('offer_id', offerId)
    .order('position', { ascending: true });
  if (errServ) throw errServ;

  return {
    ...offer,
    oc_documents: normalizeOcDocuments(offer?.oc_documents),
    services: services || [],
  };
}

/* ================================================================
   CREAR OFERTA
   >>> Incluye: service_type, certification, contact_name, contact_email
================================================================ */
export async function createOffer({
  client_id,
  notes = null,
  services = [],
  vat_rate = 0.21,
  discount = 0,
  discount_type = 'percentage',
  service_type = 'Laboratorio de Calibración',
  certification = null,
  contact_name = null,
  contact_email = null,
  validity_date = null,
  delivery_place = null,
  delivery_time = null,
  payment_method = null,
  price_currency = 'USD BNA',
  iva_rate = 21,
  special_conditions = null,
  commercial_condition,
  pdf_overrides = null,
}) {
  const { subtotal, tax, total } = calcTotals(services, vat_rate, discount, discount_type);
  const cc =
    commercial_condition ??
    abbreviateCond({ payment_method, delivery_place, price_currency });

  const { data: created, error: insErr } = await supabase
    .from('offers')
    .insert({
      client_id,
      notes,
      subtotal,
      tax,
      total,
      discount: Number(discount) || 0,
      discount_type: discount_type || 'percentage',
      status: 'draft',
      service_type,
      certification,
      contact_name,
      contact_email,
      validity_date,
      delivery_place,
      delivery_time,
      payment_method,
      price_currency,
      iva_rate,
      special_conditions,
      commercial_condition: cc,
      pdf_overrides: pdf_overrides ?? null,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;

  const offerId = created.id;

  const rows = (services || [])
    .filter((s) => (s.description || '').trim())
    .map((s, i) => ({
      offer_id: offerId,
      instrument_id: s.instrument_id ?? null,
      description: s.description.trim(),
      qty: Number(s.qty || 1),
      price: Number(s.price || 0),
      discount: Number(s.discount || 0),
      discount_type: s.discount_type || 'percentage',
      position: i,
    }));

  if (rows.length) {
    const { error: rowsErr } = await supabase.from('offer_services').insert(rows);
    if (rowsErr) throw rowsErr;
  }

  return created;
}

/* ================================================================
   ACTUALIZAR OFERTA
================================================================ */
export async function updateOffer(
  offerId,
  {
    notes = null,
    services = [],
    vat_rate = 0.21,
    discount = 0,
    discount_type = 'percentage',
    service_type = 'Laboratorio de Calibración',
    certification = null,
    contact_name = null,
    contact_email = null,
    validity_date = null,
    delivery_place = null,
    delivery_time = null,
    payment_method = null,
    price_currency = 'USD BNA',
    iva_rate = 21,
    special_conditions = null,
    commercial_condition,
    pdf_overrides = undefined,
    revision,
  }
) {
  const { subtotal, tax, total } = calcTotals(services, vat_rate, discount, discount_type);
  const cc =
    commercial_condition ??
    abbreviateCond({ payment_method, delivery_place, price_currency });

  const patch = {
    notes,
    subtotal,
    tax,
    total,
    discount: Number(discount) || 0,
    discount_type: discount_type || 'percentage',
    service_type,
    certification,
    contact_name,
    contact_email,
    validity_date,
    delivery_place,
    delivery_time,
    payment_method,
    price_currency,
    iva_rate,
    special_conditions,
    commercial_condition: cc,
    updated_at: new Date().toISOString(),
  };

  if (revision !== undefined) patch.revision = Number(revision);
  if (pdf_overrides !== undefined) patch.pdf_overrides = pdf_overrides;

  const { error: updErr } = await supabase.from('offers').update(patch).eq('id', offerId);
  if (updErr) throw updErr;

  const { error: delErr } = await supabase.from('offer_services').delete().eq('offer_id', offerId);
  if (delErr) throw delErr;

  const rows = (services || [])
    .filter((s) => (s.description || '').trim())
    .map((s, i) => ({
      offer_id: offerId,
      instrument_id: s.instrument_id ?? null,
      description: s.description.trim(),
      qty: Number(s.qty || 1),
      price: Number(s.price || 0),
      discount: Number(s.discount || 0),
      discount_type: s.discount_type || 'percentage',
      position: i,
    }));

  if (rows.length) {
    const { error: insErr } = await supabase.from('offer_services').insert(rows);
    if (insErr) throw insErr;
  }

  return { ok: true, subtotal, tax, total };
}

/* ================================================================
   GUARDAR OVERRIDES DEL PDF
================================================================ */
export async function updateOfferPdfOverrides(offerId, overrides) {
  const { error, data } = await supabase
    .from('offers')
    .update({ pdf_overrides: overrides ?? null, updated_at: new Date().toISOString() })
    .eq('id', offerId)
    .select('id')
    .single();
  if (error) throw error;
  return { ok: true, id: data?.id };
}

/* ================================================================
   CAMBIAR ESTADO
================================================================ */
export async function updateOfferStatus(offerId, status) {
  const valid = ['draft', 'sent', 'accepted', 'billing', 'closed', 'rejected'];
  if (!valid.includes(status)) throw new Error('Estado inválido');

  const { error } = await supabase
    .from('offers')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', offerId);

  if (error) throw error;
  return { ok: true };
}

/* ================================================================
   ACEPTACIÓN
================================================================ */
export async function setOfferAcceptance(offerId, payload = {}) {
  const via = payload.via ?? payload.channel ?? null;
  const oc_number = via === 'oc' ? (payload.oc_number ?? payload.reference ?? null) : null;

  const data = {
    accepted_via: via,
    oc_number,
    status: 'accepted',
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('offers').update(data).eq('id', offerId);
  if (error) throw error;
  return { ok: true };
}

/* ================================================================
   CREAR OS DESDE OFERTA (si existe la RPC)
================================================================ */
export async function createSOFromOffer(offerId) {
  const { data, error } = await supabase.rpc('create_so_from_offer', { p_offer_id: offerId });
  if (error) throw error;
  return data;
}
export async function acceptOfferCreateOS(offerId) {
  return createSOFromOffer(offerId);
}

/* ================================================================
   SUBIR OC / ANEXAR OC
================================================================ */
export async function uploadClientOC(offer, file) {
  if (!offer?.id) throw new Error('Falta la oferta');
  if (!file) throw new Error('Falta archivo');

  const currentDocs = normalizeOcDocuments(offer?.oc_documents);
  const next = [
    ...currentDocs,
    {
      id: generateLocalId('oc'),
      oc_number: safeText(offer?.oc_number) || null,
      file,
    },
  ];

  return saveOfferOcDocuments(offer, next);
}

/* ================================================================
   HISTORIAL DE OFERTA (AUDIT)
================================================================ */
export async function listOfferHistory(offerId) {
  const from = 'offer_audit';

  const { data, error } = await supabase
    .from(from)
    .select(`
      id, action, changed_at, changed_by, revision, diff, old_data, new_data
    `)
    .eq('offer_id', offerId)
    .order('changed_at', { ascending: false })
    .limit(100);

  if (error) throw error;
  return data || [];
}

export async function getOfferItemsHistory(offerId, { limit = 100 } = {}) {
  if (!offerId) return [];
  const { data, error } = await supabase
    .from('offer_services_audit')
    .select('action, changed_at, service_id, diff')
    .eq('offer_id', offerId)
    .order('changed_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

/* ================================================================
   Reconstrucción de revisión (ítems) por fecha
================================================================ */
export async function getOfferServicesAtRevision(offerId, asOfISO) {
  if (!offerId || !asOfISO) return [];

  const { data, error } = await supabase
    .from('offer_services_audit')
    .select('action, changed_at, service_id, diff, old_data, new_data')
    .eq('offer_id', offerId)
    .lte('changed_at', asOfISO)
    .order('changed_at', { ascending: true })
    .limit(2000);
  if (error) throw error;

  const rows = Array.isArray(data) ? data : [];
  const byId = new Map();

  const norm = (raw, fallback = {}) => {
    const r = raw || {};
    return {
      id: r.id ?? r.service_id ?? fallback.id ?? null,
      description: (r.description ?? r.desc ?? fallback.description ?? '').trim(),
      qty: Number(r.qty ?? r.quantity ?? fallback.qty ?? 1),
      price: Number(r.price ?? r.unit_price ?? fallback.price ?? 0),
      discount: Number(r.discount ?? fallback.discount ?? 0),
      discount_type: r.discount_type ?? fallback.discount_type ?? 'percentage',
      position: Number(r.position ?? r.order ?? fallback.position ?? 9999),
    };
  };

  for (const ev of rows) {
    const sid = ev.service_id || ev?.new_data?.id || ev?.old_data?.id;
    const prev = byId.get(sid) || null;

    if (ev.action === 'INSERT') {
      const cur = norm(ev.new_data || ev.diff?.new || {});
      if (sid == null) continue;
      byId.set(sid, { ...cur, id: sid });
      continue;
    }

    if (ev.action === 'DELETE') {
      if (sid == null) continue;
      byId.delete(sid);
      continue;
    }

    if (sid == null) continue;
    const base = prev || norm(ev.old_data || ev.diff?.old || {});
    const next = {
      ...base,
      ...norm(ev.new_data || {}, base),
    };
    byId.set(sid, next);
  }

  const out = Array.from(byId.values());
  out.sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  return out;
}

/* ================================================================
   Snapshot de cabecera desde una fila de audit
================================================================ */
export function buildOfferSnapshotFromAuditRow(row, currentOffer = null) {
  const src = row?.new_data ?? row?.old_data ?? {};
  const co = currentOffer || {};

  return {
    ...co,
    ...src,
    client: src.client ?? co.client ?? null,
    commercial_condition: src.commercial_condition ?? co.commercial_condition ?? null,
    payment_method: src.payment_method ?? co.payment_method ?? null,
    delivery_place: src.delivery_place ?? co.delivery_place ?? null,
    delivery_time: src.delivery_time ?? co.delivery_time ?? null,
    validity_date: src.validity_date ?? co.validity_date ?? null,
    price_currency: src.price_currency ?? co.price_currency ?? null,
    iva_rate: src.iva_rate ?? co.iva_rate ?? null,
    special_conditions: src.special_conditions ?? co.special_conditions ?? null,
    pdf_overrides: src.pdf_overrides ?? co.pdf_overrides ?? null,
  };
}
